import {
  type GetWorkspacesSearchArgs,
  useGetCurrentUserQuery,
  useGetTagsQuery,
  useInfiniteScroll,
  useJoinWorkspaceMutation,
  useLazyGetWorkspacesSearchQuery,
} from '@carboncopyco/data';
import type {
  Sort,
  Workspace,
  WorkspaceAccess,
  WorkspaceType,
} from '@carboncopyco/types';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useSet } from '@uidotdev/usehooks';
import constate from 'constate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ViewMode } from '../../types';
import { workspaceModalLabels } from '../WorkspaceLeaveModal';
import type { WorkspaceState } from './consts';

export type WorkspacesPageContextProps = {
  organizationId: string;
  navigateToWorkspace: (id: string) => void;
  getWorkspaceLink: (id: string) => string;
};

function useWorkspacesPageContext({
  organizationId,
  navigateToWorkspace,
  getWorkspaceLink,
}: WorkspacesPageContextProps) {
  const [joinWorkspace] = useJoinWorkspaceMutation();
  const {
    data: { user },
  } = useGetCurrentUserQuery();
  const { data: tags } = useGetTagsQuery(
    { organizationId },
    { skip: user?.role === 'presenter' }
  );

  const [query, setQuery] = useState('');
  const [selectedSort, setSelectedSort] = useState<Sort>('alphabet');
  const [selectedState, setSelectedState] = useState<WorkspaceState>('active');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const selectedTypeFilter = useSet<WorkspaceType>();
  const selectedAccessFilter = useSet<WorkspaceAccess>();
  const selectedTagsFilter = useSet<string>();

  const [isLeaveModalOpen, setOpenLeaveModal] = useState(false);
  const [isArchiveModalOpen, setOpenArchiveModal] = useState(false);
  const [isSettingsModalOpen, setOpenSettingsModal] = useState(false);
  const [isMembersModalOpen, setOpenMembersModal] = useState(false);
  const [isManageTagsOpen, setOpenManageTags] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<Workspace>(null);
  const [archiveModalLabel, setArchiveModalLabel] = useState(
    workspaceModalLabels.archive
  );

  const tagsFilterOptions = tags?.map((tag) => ({
    id: tag.id,
    label: tag.name,
  }));

  const fetchArgs = useMemo<GetWorkspacesSearchArgs>(
    () => ({
      organizationId,
      query,
      isDeleted: selectedState === 'archived',
      sort: selectedSort,
      access: selectedAccessFilter.size > 0 ? [...selectedAccessFilter] : [],
      type: selectedTypeFilter.size > 0 ? [...selectedTypeFilter] : [],
      tags: selectedTagsFilter.size > 0 ? [...selectedTagsFilter] : [],
    }),
    [
      organizationId,
      query,
      selectedSort,
      selectedState,
      selectedAccessFilter.size,
      selectedTypeFilter.size,
      selectedTagsFilter.size,
    ]
  );

  const {
    items: workspaces,
    isLoading,
    isLoadingBatch,
    error,
    fetchFirstBatch,
    fetchNextBatch,
    removeItem,
    replaceItem,
    totalSize,
  } = useInfiniteScroll(useLazyGetWorkspacesSearchQuery, {
    preferCacheValue: false,
  });

  useEffect(() => {
    fetchFirstBatch(fetchArgs);
  }, [fetchArgs, fetchFirstBatch]);

  const handleFetchNextBatch = useCallback(
    () => fetchNextBatch(fetchArgs),
    [fetchArgs, fetchNextBatch]
  );

  const onLeaveWorkspace = () => {
    if (
      selectedWorkspace.workspaceType === 'private' &&
      selectedWorkspace?.totalMembers <= 1
    ) {
      setArchiveModalLabel(workspaceModalLabels.leaveLastMember);
      setOpenArchiveModal(true);
      return;
    }
    setOpenLeaveModal(true);
  };

  const { trackWorkspaceClickOpenArchiveModal } = useWorkspaceAnalytics(
    'all_workspaces_page'
  );

  const onArchiveWorkspace = () => {
    setArchiveModalLabel(workspaceModalLabels.archive);
    setOpenArchiveModal(true);
    trackWorkspaceClickOpenArchiveModal();
  };

  return {
    joinWorkspace,
    onArchiveWorkspace,
    onLeaveWorkspace,
    workspaceModalLabels,
    isLeaveModalOpen,
    setOpenLeaveModal,
    setOpenArchiveModal,
    isArchiveModalOpen,
    isSettingsModalOpen,
    isMembersModalOpen,
    isManageTagsOpen,
    setArchiveModalLabel,
    selectedWorkspace,
    setSelectedWorkspace,
    archiveModalLabel,
    setOpenMembersModal,
    setOpenSettingsModal,
    setOpenManageTags,
    totalSize,
    isLoading,
    isLoadingBatch,
    error,
    handleFetchNextBatch,
    workspaces,
    organizationId,
    query,
    setQuery,
    navigateToWorkspace,
    getWorkspaceLink,
    selectedTypeFilter,
    resetSelectedTypeFilter: () => selectedTypeFilter.clear(),
    selectedAccessFilter,
    resetSelectedAccessFilter: () => selectedAccessFilter.clear(),
    tags,
    tagsFilterOptions,
    selectedTagsFilter,
    resetSelectedTagsFilter: () => selectedTagsFilter.clear(),
    selectedSort,
    setSelectedSort,
    replaceItem,
    removeItem,
    selectedState,
    setSelectedState,
    setViewMode,
    viewMode,
    clearAll: () => {
      setQuery('');
      setSelectedSort('alphabet');
      setSelectedState('active');
      selectedAccessFilter.clear();
      selectedTypeFilter.clear();
      selectedTagsFilter.clear();
    },
    isDirty:
      query ||
      selectedState === 'archived' ||
      selectedAccessFilter.size > 0 ||
      selectedTypeFilter.size > 0 ||
      selectedTagsFilter.size > 0,
  };
}

export const [WorkspacesPageProvider, useWorkspacesPage] = constate(
  useWorkspacesPageContext
);
